import React, { useState, useRef, useEffect, useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { get, isEqual } from 'lodash';
import styles from './dropdown.module.scss';
import { Icons } from '../';
import { useAfterFirstRender } from '../../hooks/useAfterFirstRender';
import {
  applyClasses,
  applyProps,
  useKeyPress,
  constants,
  isTablet,
} from '../shared/js';
import Options from './Options';
import Option from './Option';
const { ESCAPE_KEY } = constants;

const SEARCH_WAIT_TIME = 500;

const Dropdown = (props) => {
  const formContext = useFormContext();
  if (formContext) {
    return <FormDropdown {...props} />;
  }
  return <SimpleDropdown {...props} />;
};

const FormDropdown = (props) => {
  const value = useWatch({ name: props.name });
  return <SimpleDropdown {...props} value={value} />;
};

const SimpleDropdown = ({
  id,
  placeholder,
  name,
  options,
  defaultValue,
  onChange,
  validation,
  label,
  mobileLabel,
  disabled,
  value,
  disableAutoResetOnOptionsChange,
  dirtyErrors,
  submitErrors,
  dirtyTouchedErrors,
  ...props
}) => {
  const toggleRef = useRef();
  const wrapperRef = useRef();
  const searchTimeRef = useRef();
  const formMethods = useFormContext();
  const formFieldValue = get(formMethods?.getValues(), name);
  const computedDefaultVal = formFieldValue ?? defaultValue ?? '';
  const [fieldValue, setFieldValue] = useState(computedDefaultVal);

  //sync Form's State fieldValue with local fieldValue State
  useEffect(() => {
    if (formMethods && formFieldValue !== fieldValue) {
      setFieldValue(formFieldValue);
    }
  }, [formFieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

  let defaultLabel = placeholder;
  if (computedDefaultVal) {
    const option = options.find(
      (option) => option.value === computedDefaultVal
    );
    defaultLabel = option?.selectedLabel || defaultLabel;
  }

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownClicked, setDropdownClicked] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [searchedIndex, setSearchedIndex] = useState(-1);
  const escPressed = useKeyPress(ESCAPE_KEY);
  const register = formMethods?.register ? formMethods?.register : () => {};
  const setFormValue = formMethods?.setValue ? formMethods?.setValue : () => {};

  const errors = formMethods?.formState.errors;
  const error = errors && get(errors, name);
  const errorType = error?.type;
  const isDirty = !!get(formMethods?.formState.dirtyFields, name);
  const isTouched = !!get(formMethods?.formState.touchedFields, name);
  const isSubmitted = formMethods?.formState.isSubmitted;
  const shouldShowError = useMemo(() => {
    if (!errorType) return false;
    if (!isDirty && dirtyErrors.includes(errorType)) {
      return false;
    }
    if (!isDirty && !isTouched && dirtyTouchedErrors.includes(errorType)) {
      return false;
    }
    if (!isSubmitted && submitErrors.includes(errorType)) {
      return false;
    }
    return true;
  }, [
    isDirty,
    isTouched,
    isSubmitted,
    dirtyErrors,
    dirtyTouchedErrors,
    submitErrors,
    errorType,
  ]);

  useEffect(() => {
    if (!dropdownOpen) return;
    escPressed && setDropdownOpen(false);
  }, [escPressed, dropdownOpen]);

  useEffect(() => {
    if (disableAutoResetOnOptionsChange) return;
    if (defaultValue) {
      setFormValue(name, defaultValue);
    }
  }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const optionsValuesHash = options.map((o) => `${o.value}`).join();
  useAfterFirstRender(() => {
    if (disableAutoResetOnOptionsChange) return;
    setFieldValue();
    setFormValue(name, null);
  }, [optionsValuesHash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchString) return;
    const match = options.findIndex(
      (option) => option.label.toLowerCase?.().indexOf(searchString) === 0
    );
    if (match < 0) setSearchedIndex(null);
    else setSearchedIndex(match);
  }, [searchString]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldUseValue = (value) => ![undefined, null].includes(value);

  const findCorrespondingOptionLabel = (value, options) => {
    const correspondingOption = options.find((option) =>
      isEqual(value, option.value)
    );
    if (correspondingOption) {
      return correspondingOption.selectedLabel;
    } else if (value === '') {
      return defaultLabel;
    }
  };

  const selectOption = (option) => {
    setFieldValue(option.value);
    setDropdownOpen(false);
    toggleRef.current.focus();
    onChange && onChange(option.value);
    setFormValue(name, option.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    setDropdownClicked(true);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setDropdownClicked(false);
  };

  const shouldCloseDropdown = (e) => {
    if (isTablet()) {
      return e.relatedTarget && !wrapperRef.current.contains(e.relatedTarget);
    }
    return !wrapperRef.current.contains(e.relatedTarget);
  };

  const leaveDropdown = (e) => {
    if (shouldCloseDropdown(e)) {
      closeDropdown();
    }
  };

  const displayValue = findCorrespondingOptionLabel(
    value || fieldValue,
    options
  );

  const handleKeyPress = (e) => {
    // Control keys have a length greater than 1, and we don't wan't to use them to search
    if (!dropdownOpen || e.key.length > 1) return;
    e.preventDefault();
    const now = new Date().getTime();
    if (
      !searchTimeRef.current ||
      now - searchTimeRef.current > SEARCH_WAIT_TIME
    ) {
      setSearchString(e.key.toLowerCase());
    } else {
      setSearchString(searchString + e.key.toLowerCase());
    }

    searchTimeRef.current = now;
  };

  return (
    <>
      <div
        onKeyDown={handleKeyPress}
        onBlur={leaveDropdown}
        ref={wrapperRef}
        className={cn(
          styles.dropdown,
          // eslint-disable-next-line react/prop-types
          props.className,
          { [styles.open]: dropdownOpen },
          applyClasses(props, styles)
        )}
      >
        {label && <label htmlFor={`dropdown_${name}`}>{label}</label>}
        <input
          id={`dropdown_${name}`}
          name={name}
          type="hidden"
          {...register(name, { ...validation })}
          {...applyProps(props)}
        />
        <button
          id={id}
          ref={toggleRef}
          className={cn({ [styles.focused]: dropdownClicked }, styles.toggle)}
          onClick={!disabled ? toggleDropdown : undefined}
          type="button"
          disabled={disabled}
        >
          <div className={styles.label}>{displayValue || placeholder}</div>
          <Icons.DropdownArrow
            className={cn(styles.arrow, { [styles.open]: dropdownOpen })}
          />
        </button>
        <Options
          fullScreen
          options={options}
          fieldValue={shouldUseValue(value) ? value : fieldValue}
          onClick={selectOption}
          isOpen={dropdownOpen}
          onClose={closeDropdown}
          id={id}
          label={mobileLabel ? mobileLabel : label}
          className={props.optionsClassName}
          optionClassName={props.optionClassName}
          searchedIndex={searchedIndex}
        />
      </div>
      {error && shouldShowError && (
        <span role="alert">
          {error.message || (
            <FormattedMessage
              id="forms.error"
              description="Error message"
              defaultMessage="Field error"
            />
          )}
        </span>
      )}
    </>
  );
};

Dropdown.propTypes = {
  /** The ID of the toggle. Useful to select the input during testing. Also used as a prefix for its options' IDs */
  id: PropTypes.string,
  /** Function to run when the selected value changes */
  onChange: PropTypes.func,
  /** The default value of the input */
  defaultValue: PropTypes.any,
  /** The placeholder label of the dropdown, the default of the hidden input is null */
  placeholder: PropTypes.any,
  /** The name of the input for use in a form */
  name: PropTypes.string,
  /** The options available in the dropdown */
  options: PropTypes.arrayOf(PropTypes.object),
  /** Applies primary styles to the dropdown */
  primary: PropTypes.bool,
  /** Applies secondary styles to the dropdown */
  secondary: PropTypes.bool,
  /** Makes the dropdown fill the width of its container */
  fullWidth: PropTypes.bool,
  /** The validation rules for the input, see https://react-hook-form.com/get-started#Applyvalidation */
  validation: PropTypes.object,
  /** The form errors object */
  errors: PropTypes.object,
  /** A custom label for the input */
  label: PropTypes.any,
  /** Whether or not the dropdown is active */
  isActive: PropTypes.bool,
  /** Disable the input */
  disabled: PropTypes.bool,
  /** Overrides the display of the selected label; if there is a matching option, its label will be used */
  value: PropTypes.any,
  /** The label shown in the mobile version of the dropdown. Will default to label if not provided */
  mobileLabel: PropTypes.any,
  /** disable the resetting of the default value when the options change */
  disableAutoResetOnOptionsChange: PropTypes.bool,
  dirtyErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
  dirtyTouchedErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
  submitErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
};

Dropdown.defaultProps = {
  onChange: undefined,
  defaultValue: '',
  placeholder: (
    <FormattedMessage id="placeholders.select" defaultMessage="Select" />
  ),
  name: undefined,
  options: [],
  primary: false,
  secondary: false,
  fullWidth: false,
  validation: {},
  errors: {},
  label: undefined,
  disabled: false,
  value: undefined,
  disableAutoResetOnOptionsChange: false,
  dirtyErrors: [],
  dirtyTouchedErrors: [],
  submitErrors: [],
};

Dropdown.Options = Options;
Dropdown.Option = Option;

FormDropdown.propTypes = Dropdown.propTypes;
FormDropdown.defaultProps = Dropdown.defaultProps;

SimpleDropdown.propTypes = Dropdown.propTypes;
SimpleDropdown.defaultProps = Dropdown.defaultProps;

export default Dropdown;
